import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useMemo } from 'react';

import {
  eventHasVisibleChanges,
  mergeEventsByActionId,
  resolveEffectiveRateTypes,
} from '~components/Job/LoadPricing/pricingEventsHelpers';
import PricingEventsTable from '~components/Job/LoadPricing/PricingEventsTable';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import usePricingEventsQuery from '~queries/usePricingEventsQuery';
import { Nullable } from '~types/Nullable';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

interface PricingEventsTimelineProps {
  subjectId?: Nullable<string>;
  /** Defaults to 'Load' — the POC surfaces per-Load pricing history. */
  subjectType?: string;
  /**
   * Job-grain timeline — the job plus its JobAssignments and Loads. Overrides
   * the subject filter; events are collapsed by action_id so one cascade reads
   * as a single entry instead of repeating per Load.
   */
  jobId?: Nullable<string>;
  /**
   * Order-grain timeline — the whole Order subtree (Order + Jobs + JobAssignments
   * + Loads). `jobId` takes precedence; otherwise this overrides the subject filter.
   */
  orderId?: Nullable<string>;
}

/**
 * Read-only history of `/v1/pricing_events` for one object, rendered as a table. Events sharing an
 * action_id are merged into a single entry carrying every change (one logical change fans out
 * across grains, and splits by audience on counterparty orders), and each event's `changes` are
 * grouped by billing party.
 */
export default function PricingEventsTimeline({
  subjectId,
  subjectType = 'Load',
  jobId,
  orderId,
}: PricingEventsTimelineProps) {
  const theme = useTheme();
  const loadLevelPricingEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.loadLevelPricing,
  });
  const { data: events, isLoading } = usePricingEventsQuery({
    subjectType,
    subjectId,
    jobId,
    orderId,
  });

  // Load-grain pricing is an unfinished project, so its events are withheld from the order/job
  // timeline until `load_level_pricing` ships. They are actively misleading here: one
  // `load_divergence` action means EITHER the load took its own rates (re-match) or gave them up
  // and fell back to the job's (re-align), and the timeline renders both identically — a row that
  // reads "rates updated" can mean the exact opposite.
  //
  // Dropping them costs no history: `reevaluate_load_divergence!` re-uses the cascade's `action_id`,
  // so the cascade's Order/JobAssignment records carry the same changes. Filtered BEFORE the merge
  // so a Load record can never become an entry's identity. The load-grain entry points are
  // themselves flag-gated (TRE-15449), so this cannot blank a view the user can still reach.
  const scopedEvents = useMemo(
    () =>
      loadLevelPricingEnabled
        ? events
        : events?.filter((event) => event.subjectType !== 'Load'),
    [events, loadLevelPricingEnabled],
  );

  // Collapse the records of one atomic change into a single entry, keeping every distinct change.
  // Each entry pairs the event that names it with how many jobs the action reached.
  const mergedEntries = useMemo(
    () => (scopedEvents ? mergeEventsByActionId(scopedEvents) : []),
    [scopedEvents],
  );

  // Hide events that have nothing to show (e.g. only an internal id change), so the panel never
  // renders an event with no underlying change rows.
  const visibleEntries = useMemo(
    () => mergedEntries.filter((entry) => eventHasVisibleChanges(entry.event)),
    [mergedEntries],
  );

  // Resolve rate types over the FULL merged list (not just visibleEntries) so a value-only edit's
  // carried-forward type survives even if the type-setting event was filtered out of the view.
  const effectiveRateTypesByEvent = useMemo(
    () => resolveEffectiveRateTypes(mergedEntries.map((entry) => entry.event)),
    [mergedEntries],
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <LoadingSpinner isVisible loadingIndicatorSize={16} />
      </Box>
    );
  }

  if (!visibleEntries.length) {
    return (
      <Typography variant="body2" sx={{ color: theme.brandV2.colors.treadGray3, py: 1 }}>
        {t('loads.pricing.no_pricing_events')}
      </Typography>
    );
  }

  return (
    <PricingEventsTable
      entries={visibleEntries}
      effectiveRateTypesByEvent={effectiveRateTypesByEvent}
    />
  );
}
